<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<template>
  <div
    :class="{ 'base-switch': true, 'base-switch--disabled': disabled, error }"
  >
    <div class="switch-holder">
      <div
        v-if="label instanceof Array || labelLeftSlot"
        class="label label--left"
      >
        <span>{{ getLeftLabel }}</span>
        <slot name="label:left" />
      </div>
      <div
        role="switch"
        class="accessibility-outline"
        :class="{ 'switch rounded': true, active: isActive }"
        tabindex="0"
        :tabindex="disabled ? -1 : 0"
        :aria-disabled="disabled"
        :aria-label="ariaLabel || 'Auswahl'"
        :aria-checked="isActive ? 'true' : 'false'"
        @click.stop="toggle"
        @keypress.enter.stop="toggle"
        @keydown.space.stop="toggle"
      >
        <input
          v-model="internalValue"
          type="checkbox"
        >
        <div class="switch--bar" />
      </div>
      <div
        v-if="label || labelRightSlot"
        class="label label--right"
      >
        <span>{{ getRightLabel }}</span>
        <slot name="label:right" />
      </div>
    </div>
    <div ref="content">
      <slot name="content" />
    </div>
    <div
      v-show="error"
      class="base-switch--error text-danger"
      v-html="error"
    />
  </div>
</template>

<script lang="ts" setup>
import {
  computed, defineProps, defineEmits, ref, watch, onMounted, useSlots, defineExpose,
} from 'vue';

const props = defineProps({
  label: {
    type: [String, Array, Object],
    default: '',
  },
  value: {
    type: [Boolean, String, Number],
    default: false,
  },
  states: {
    type: Array,
    default: null,
  },
  filled: {
    type: Object,
    required: false,
    default: () => ({
      active: true,
      inactive: false,
    }),
  },
  error: {
    type: String,
    default: '',
  },
  switchAlignment: {
    type: String,
    default: 'center',
  },
  toNumber: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  visibleState: {
    type: Boolean,
    default: null,
  },
});

const emit = defineEmits(['update', 'input']);
const slots = useSlots();

const labelLeftSlot = computed(() => slots['label:left']);
const labelRightSlot = computed(() => slots['label:right']);

const ariaLabel = computed(() => {
  if (props.label instanceof Array) {
    return `Linkes Label: ${props.label[0]}, Rechtes Label: ${props.label[1]}`;
  }
  if (props.label) {
    return props.label as string;
  }

  return 'Auswahl';
});

const isSwitchActive = (value = props.value): boolean => {
  const validValues = props.states ? [props.states[1]] : [1, true, '1'];

  return validValues.includes(value);
};

const getCurrentValue = (value = props.value): boolean => {
  if (props.states) {
    return props.states[1] === value
      ? props.states[1] as boolean
      : props.states[0] as boolean;
  }

  return [1, true, '1'].indexOf(value) >= 0;
};

const currentSwitchValue = ref(getCurrentValue());
const internalValue = ref(isSwitchActive());

const filledColor = computed(() => Object.entries({
  active: false,
  inactive: false,
}).reduce((acc: Record<string, string>, [key, value]) => {
  const val = props.filled[key] || value;
  if (typeof val === 'boolean') {
    if (val) {
      acc[key] = 'var(--company-main-primary)';
    } else {
      acc[key] = '#DBDBDB';
    }
  } else if (typeof val === 'string') {
    acc[key] = val.includes('#')
      ? val
      : `var(--${val})`;
  }

  return acc;
}, {}));

const switchValue = computed({
  get(): boolean {
    return currentSwitchValue.value;
  },
  set(state) {
    const newValue = props.toNumber ? Number(state) : state;

    currentSwitchValue.value = getCurrentValue(state);
    internalValue.value = isSwitchActive(newValue);
    emit('input', newValue);
    emit('update', newValue);
  },
});
const getLeftLabel = computed((): string | null => {
  const label = props.label instanceof Array
    ? props.label[0] as string
    : null;

  return label;
});
const getRightLabel = computed((): string => {
  const label = props.label instanceof Array
    ? props.label[1] as string
    : props.label as string | Record<string, string>;

  return label instanceof Object ? label[Number(switchValue.value)] : label;
});
const isActive = computed(() => (
  props.visibleState !== null
    ? props.visibleState
    : internalValue.value
));

watch(() => props.value, (value) => {
  if (value !== switchValue.value) {
    switchValue.value = isSwitchActive(value);
  }
});

onMounted(() => {
  emit('update', props.toNumber ? Number(switchValue.value) : switchValue.value);
});

const toggle = () => {
  if (props.states) {
    switchValue.value = switchValue.value === props.states[0]
      ? props.states[1] as boolean
      : props.states[0] as boolean;
  } else {
    switchValue.value = !switchValue.value as boolean;
  }
};

defineExpose({
  filledColor,
  switchValue,
  isActive,
  toggle,
});
</script>

<style lang="scss" scoped>
$bar-width: 3.625em;
$bar-height: 1.3125em;
$indicator-size: 1.4375em;

.base-switch {
    position: relative;
    display: flex;
    align-items: center;
    font-size: 1rem;
    font-size: clamp(16px, 1rem, 32px);
    flex-wrap: wrap;

    .switch-holder {
        display: flex;
        flex-wrap: nowrap;
        align-items: v-bind(switchAlignment);

        > .label {
            flex: 0 0 auto;

            &--left {
                padding-right: .625em;
                line-break: normal;
                flex: 0 1 auto;
            }

            &--right {
                padding-left: .625em;
                line-break: normal;
                flex: 0 1 auto;
            }
        }
    }

    &.error .label {
        color: var(--forms-errors);
    }

    &--disabled {
        opacity: .5;
        pointer-events: none;
    }

    &--error {
        padding: 0.25em 0;
        flex: 1 0 100%;
        width: 100%;
        min-width: 100%;
        font-size: 12px;
    }

    .switch {
        position: relative;
        cursor: pointer;
        flex: 0 0 #{$bar-width};
        padding: 0;
        height: fit-content;

        input {
            all: unset;
            display: none;
        }

        &--bar {
            position: relative;
            width: #{$bar-width};
            min-width: #{$bar-width};
            height: $bar-height;
            background-color: v-bind('filledColor.inactive');
            overflow: hidden;

            &:before {
                position: absolute;
                content: '';
                height: 100%;
                width: 0;
                transition: width .3s ease-in-out;
            }
        }

        &:after {
            content: '';
            display: block;
            position: absolute;
            width: $indicator-size;
            height: $indicator-size;
            left: -.6px;
            top: 50%;
            transform: translateY(-50%);
            transition: left .3s ease-in-out;
            background-color: #fff;
            box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
        }

        &.active {
            .switch--bar {
                &:before {
                    width: #{$bar-width};
                    background-color: v-bind('filledColor.active');
                }
            }

            &:after {
                left: calc(100% - #{$indicator-size} + .6px);
            }
        }

        &.rounded {
            &:after, .switch--bar {
                border-radius: 999px !important;
            }
        }
    }
}
</style>
